import { useQuery } from "@apollo/client";
import {
  Alert,
  Form,
  InnerForm,
  Modal,
  Table,
  TextInput,
} from "@fluidattacks/design";
import type { ColumnDef } from "@tanstack/react-table";
import type { GraphQLError } from "graphql";
import { Fragment, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  IConvertMailmapSubentriesToNewEntryModalProps,
  IConvertMailmapSubentryToNewEntryFormValues,
  IMailmapSubentry,
} from "../types";
import { useTable } from "hooks/use-table";
import { GET_MAILMAP_ENTRY_SUBENTRIES_QUERY } from "pages/organization/mailmap/queries";
import { Logger } from "utils/logger";
import { msgError } from "utils/notifications";

const ConvertMailmapSubentriesToNewEntryModal: React.FC<
  IConvertMailmapSubentriesToNewEntryModalProps
> = ({
  onClose,
  onSubmit,
  entryEmail,
  subentryEmail,
  subentryName,
  organizationId,
  modalRef,
}: IConvertMailmapSubentriesToNewEntryModalProps): JSX.Element => {
  const { t } = useTranslation();

  const columns = useMemo(
    (): ColumnDef<IMailmapSubentry>[] => [
      {
        accessorKey: "mailmapSubentryName",
        header: t("mailmap.subentryName"),
      },
      {
        accessorKey: "mailmapSubentryEmail",
        header: t("mailmap.subentryEmail"),
      },
    ],
    [t],
  );

  const { data } = useQuery(GET_MAILMAP_ENTRY_SUBENTRIES_QUERY, {
    fetchPolicy: "cache-first",
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error: GraphQLError): void => {
        if (error.message.startsWith("Exception - Mailmap entry not found:")) {
          msgError(
            t("mailmap.errorMailmapEntryNotFound"),
            t("mailmap.errorTitle"),
          );
          Logger.error("Exception - Mailmap entry not found:", error);
        } else {
          msgError(
            t("mailmap.errorGetMailmapSubentries"),
            t("mailmap.errorTitle"),
          );
          Logger.error(
            `Error loading mailmap subentries for ${entryEmail}:`,
            error,
          );
        }
      });
    },
    variables: { entryEmail, organizationId },
  });

  const subentries: IMailmapSubentry[] =
    data?.mailmapEntrySubentries?.map(
      (subentry): IMailmapSubentry => ({
        id: subentry.mailmapSubentryEmail,
        mailmapSubentryEmail: subentry.mailmapSubentryEmail,
        mailmapSubentryName: subentry.mailmapSubentryName,
      }),
    ) ?? [];

  // Filter out selected subentry (main subentry)
  const filteredSubentries = subentries.filter(
    (subentry: IMailmapSubentry): boolean =>
      !(
        subentry.mailmapSubentryEmail === subentryEmail &&
        subentry.mailmapSubentryName === subentryName
      ),
  );

  const { table } = useTable({
    columns,
    data: filteredSubentries,
    id: `mailmap-convert-subentries-modal-${entryEmail}`,
    options: {
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: {
        enableMultiSelection: true,
      },
    },
    total: filteredSubentries.length,
  });

  const onCloseModal = useCallback((): void => {
    table.setRowSelection({});
    onClose();
  }, [table, onClose]);

  const handleSubmit = useCallback(
    async (
      values: IConvertMailmapSubentryToNewEntryFormValues,
    ): Promise<void> => {
      const { rowSelection } = table.getState();
      const { data: tableData } = table.options.meta ?? {};
      const selectedSubentries: IMailmapSubentry[] = Object.keys(rowSelection)
        .map((key): IMailmapSubentry | null => {
          const entry = tableData?.get(key);

          if (!entry) return null;

          return {
            mailmapSubentryEmail: entry.mailmapSubentryEmail,
            mailmapSubentryName: entry.mailmapSubentryName,
          };
        })
        .filter((entry): entry is IMailmapSubentry => entry !== null);

      return onSubmit({ ...values, otherSubentries: selectedSubentries });
    },
    [table, onSubmit],
  );

  const noAvailableSubentries = filteredSubentries.length === 0;

  return (
    <Modal
      modalRef={modalRef}
      onClose={onCloseModal}
      size={"sm"}
      title={t("mailmap.convertMailmapSubentriesToNewEntry")}
    >
      <Form
        cancelButton={{ onClick: onCloseModal }}
        confirmButton={{ label: t("mailmap.convertSubentriesToNewEntry") }}
        defaultDisabled={noAvailableSubentries}
        defaultValues={{
          entryEmail,
          mainSubentry: {
            mailmapSubentryEmail: subentryEmail,
            mailmapSubentryName: subentryName,
          },
          otherSubentries: [],
        }}
        onSubmit={handleSubmit}
      >
        <InnerForm>
          {({ register }): JSX.Element => (
            <Fragment>
              <TextInput
                disabled={true}
                label={t("mailmap.convertNewEntryName")}
                name={"mainSubentry.mailmapSubentryName"}
                placeholder={t("mailmap.subentryName")}
                register={register}
              />
              <TextInput
                disabled={true}
                label={t("mailmap.convertNewEntryEmail")}
                name={"mainSubentry.mailmapSubentryEmail"}
                placeholder={t("mailmap.subentryEmail")}
                register={register}
              />
              {noAvailableSubentries ? (
                <Alert>
                  {t("mailmap.alertNoAvailableSubentriesToConvert")}
                </Alert>
              ) : (
                <Alert variant={"info"}>
                  {t("mailmap.infoConvertSubentriesToNewEntry2")}
                </Alert>
              )}
              <Table table={table}>
                <Table.Content />
              </Table>
              {noAvailableSubentries ? undefined : (
                <Alert variant={"warning"}>
                  {t("mailmap.alertConvertSubentriesToNewEntry")}
                </Alert>
              )}
            </Fragment>
          )}
        </InnerForm>
      </Form>
    </Modal>
  );
};

export { ConvertMailmapSubentriesToNewEntryModal };
